import './App.css';
import { Component, useState, useEffect, useCallback } from 'react';
import { BASE, SURFACE, LIGHT, MUTE, FAINT, GHOST, GOLD, EMBER, DISPLAY, BODY, MONO, BACKEND_URL, API as API_BASE, applyTheme } from './lib/constants';

// Error Boundary to catch render crashes (shows error instead of blank white page)
export class ErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  render() {
    if (this.state.error) {
      return (
        <div style={{ background: '#111', color: '#fff', padding: 40, fontFamily: 'monospace', minHeight: '100vh' }}>
          <h2 style={{ color: '#ff6b6b' }}>App Error (please share this with support)</h2>
          <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', fontSize: 13, color: '#ffcc00' }}>
            {this.state.error?.message}
          </pre>
          <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', fontSize: 11, color: '#aaa' }}>
            {this.state.error?.stack}
          </pre>
          <button onClick={() => window.location.href = '/'} style={{ marginTop: 24, padding: '10px 20px', background: '#E8A849', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 700 }}>
            Go Back to Login
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

import { Ic, GrainOverlay, Dp, Mo, Bd } from './components/shared';
import { OnboardingScreen, BankScreen } from './components/Onboarding';
import { DebtOnboarding } from './components/DebtOnboarding';
import { HomeScreen } from './components/HomeScreen';
import { StrategyScreen, WhatIfScreen, DebtEntryScreen, BudgetScreen } from './components/PlanScreens';
import { CoachScreen } from './components/CoachScreen';
import { InsightsScreen } from './components/InsightsScreen';
import { SettingsScreen, NotificationsScreen, ExportScreen, ReferralScreen, HelpScreen, MerchantRulesScreen, SetPasswordScreen } from './components/SettingsScreens';
import { PrivacyScreen } from './components/PrivacyScreen';
import { TermsScreen } from './components/TermsScreen';
import { DeleteAccountScreen } from './components/DeleteAccountScreen';
import { PaymentsScreen, TransactionDetailScreen, ManualPaymentScreen, BadgesScreen, SpendingScreen, PremiumScreen, RoundUpsScreen } from './components/SubScreens';
import { CelebrationScreen, ComebackScreen, FinaleScreen, YearReviewScreen } from './components/Celebrations';
import { NotifBell, NotificationInbox, NotifToast } from './components/NotificationCenter';
import { TimelineScreen } from './components/TimelineScreen';
import { LiteracyScreen } from './components/LiteracyCards';
import { ChallengesScreen } from './components/DailyChallenges';
import { SpendingAnalysisScreen } from './components/SpendingAnalysis';
import { LeaderboardScreen, ActivityScreen, PointsScreen, RecurringScreen, CustomStrategyScreen } from './components/SocialScreens';
import { CountdownScreen, CountdownStandalone } from './components/CountdownWidget';
import { AuthScreen } from './components/AuthScreen';
import { BankManageScreen } from './components/BankManageScreen';
import { StreakScreen } from './components/StreakTracker';
import { FinancialProfileWizard } from './components/FinancialProfileWizard';
import { DebtDetailScreen } from './components/DebtDetailScreen';
import * as api from './lib/api';
import storage from './lib/storage';
import { getCurrentPath, getCurrentOrigin, getSearchParams, getHash, navigateTo } from './lib/navigation';
import offlineCache from './lib/offlineCache';

const TABS = ['home', 'strategy', 'insights', 'coach', 'settings'];
const TAB_LABELS = { home: 'Home', strategy: 'Plan', insights: 'Insights', coach: 'Coach', settings: 'Settings' };
const TAB_ICONS = { home: Ic.home, strategy: Ic.plan, insights: Ic.search, coach: Ic.coach, settings: Ic.gear };

export default function App() {
  const [phase, setPhase] = useState('loading');
  const [tab, setTab] = useState('home');
  const [screen, setScreen] = useState(null);
  const [screenData, setScreenData] = useState(null);
  const [screenStack, setScreenStack] = useState([]);
  const [user, setUser] = useState(null);
  const [debts, setDebts] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [toast, setToast] = useState(null);
  const [theme, setThemeState] = useState('dark');
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  const [bankStatus, setBankStatus] = useState(null);
  const [editProfileData, setEditProfileData] = useState(null);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);

  // ── Online / Offline detection ──────────────────────────────────
  useEffect(() => {
    const goOffline = () => setIsOffline(true);
    const goOnline  = () => {
      setIsOffline(false);
      refreshData(); // re-fetch live data when connection restores
    };
    window.addEventListener('offline', goOffline);
    window.addEventListener('online',  goOnline);
    return () => {
      window.removeEventListener('offline', goOffline);
      window.removeEventListener('online',  goOnline);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load theme on mount + register service worker + capture install prompt
  useEffect(() => {
    api.getPreferences().then(r => {
      const t = r.data?.theme || 'dark';
      setThemeState(t);
      applyTheme(t);
    }).catch(() => applyTheme('dark'));

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js').then(reg => {
        console.log('SW registered:', reg.scope);
      }).catch(err => console.log('SW error:', err));
    }

    // Capture the PWA install prompt
    const handler = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const init = useCallback(async () => {
    // Detect Plaid OAuth redirect — oauth_state_id appears in URL when bank redirects back
    const oauthStateId = new URLSearchParams(getSearchParams()).get('oauth_state_id');

    // Handle Google OAuth callback — session_id comes in URL hash fragment
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    let sessionId = null;
    const hash = getHash();
    if (hash && hash.includes('session_id=')) {
      const hashParams = new URLSearchParams(hash.substring(1));
      sessionId = hashParams.get('session_id');
    }
    if (sessionId) {
      try {
        const res = await fetch(`${API_BASE}/auth/google-session`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ session_id: sessionId }),
          credentials: 'include'
        });
        if (res.ok) {
          const userData = await res.json();
          setUser(userData);
          window.history.replaceState({}, '', '/');
          if (userData.onboarded) {
            // Load debts in the background — don't block navigation if this fails
            try {
              const { data: d } = await api.getDebts();
              setDebts(d);
              offlineCache.setUser(userData);
              offlineCache.setDebts(d);
            } catch { }
            fetch(`${API_BASE}/plaid/status`, { credentials: 'include' }).then(r => r.ok ? r.json() : null).then(s => s && setBankStatus(s)).catch(() => {});
            setPhase('app');
          } else {
            setPhase('onboarding');
          }
          return;
        }
      } catch (e) { console.error('Google auth error:', e); }
      window.history.replaceState({}, '', '/');
    }

    // Try to restore session
    try {
      const res = await fetch(`${API_BASE}/auth/me`, { credentials: 'include' });
      if (res.ok) {
        const userData = await res.json();
        setUser(userData);

        // Plaid OAuth redirect — go straight to bank screen to complete connection
        if (oauthStateId) {
          setPhase('bank');
          return;
        }

        if (userData.onboarded) {
          const { data: d } = await api.getDebts();
          setDebts(d);
          offlineCache.setUser(userData);
          offlineCache.setDebts(d);
          fetch(`${API_BASE}/plaid/status`, { credentials: 'include' }).then(r => r.ok ? r.json() : null).then(s => s && setBankStatus(s)).catch(() => {});
        }
        setPhase('app');
        return;
      }
    } catch (e) {
      // Network failure — attempt offline cache restore
      const cachedUser  = await offlineCache.getUser();
      const cachedDebts = await offlineCache.getDebts();
      if (cachedUser) {
        setUser(cachedUser);
        if (cachedDebts) setDebts(cachedDebts);
        setIsOffline(true);
      }
    }

    // No session — show app in guest mode (no login gate)
    setPhase('app');
  }, []);

  useEffect(() => { init(); }, [init]);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/notifications/unread-count`, { credentials: 'include' });
      const data = await res.json();
      setUnreadCount(data.count || 0);
    } catch { }
  }, []);

  // Auto-generate notifications on app load and poll unread count
  useEffect(() => {
    if (phase === 'app') {
      fetch(`${API_BASE}/notifications/generate`, { method: 'POST', credentials: 'include' })
        .then(() => fetchUnreadCount())
        .then(() => {
          return fetch(`${API_BASE}/notifications/inbox`, { credentials: 'include' });
        })
        .then(r => r.json())
        .then(notifs => {
          const highPrio = notifs.find(n => !n.read && n.priority === 'high');
          if (highPrio) setToast(highPrio);
        })
        .catch(() => {});
      const interval = setInterval(fetchUnreadCount, 30000);
      return () => clearInterval(interval);
    }
  }, [phase, fetchUnreadCount]);

  const refreshData = async () => {
    try {
      const [u, d] = await Promise.all([api.getUser(), api.getDebts()]);
      setUser(u.data);
      setDebts(d.data);
      // Persist latest data for offline access
      offlineCache.setUser(u.data);
      offlineCache.setDebts(d.data);
      setIsOffline(false);
    } catch (e) {
      console.error(e);
      // On failure try to serve from cache
      const cachedDebts = await offlineCache.getDebts();
      const cachedUser  = await offlineCache.getUser();
      if (cachedDebts) setDebts(cachedDebts);
      if (cachedUser)  setUser(cachedUser);
      if (cachedDebts || cachedUser) setIsOffline(true);
    }
  };

  const navigate = (screenName, tabName, data) => {
    if (tabName && !screenName) {
      setTab(tabName);
      setScreen(null);
      setScreenData(null);
      setScreenStack([]);
      return;
    }
    if (screenName) {
      // For edit-profile, fetch existing profile data first
      if (screenName === 'edit-profile') {
        fetch(`${API_BASE}/user/financial-profile`, { credentials: 'include' })
          .then(r => r.ok ? r.json() : { profile: null })
          .then(d => setEditProfileData(d.profile || null))
          .catch(() => setEditProfileData(null));
      }
      setScreenStack(p => [...p, { screen, data: screenData }]);
      setScreen(screenName);
      setScreenData(data || null);
    }
  };

  const goBack = () => {
    if (screenStack.length > 0) {
      const prev = screenStack[screenStack.length - 1];
      setScreenStack(p => p.slice(0, -1));
      setScreen(prev.screen);
      setScreenData(prev.data);
    } else {
      setScreen(null);
      setScreenData(null);
    }
    refreshData();
    fetchUnreadCount();
  };

  const handleAuth = async (userData) => {
    setUser(userData);
    if (!userData.onboarded) {
      setPhase('debt-entry');
      return;
    }
    try {
      const { data: d } = await api.getDebts();
      setDebts(d);
      offlineCache.setUser(userData);
      offlineCache.setDebts(d);
    } catch { }
    setPhase('app');
    setTab('home');
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API_BASE}/auth/logout`, { method: 'POST', credentials: 'include' });
    } catch { }
    setUser(null);
    setDebts([]);
    setTab('home');
    setScreen(null);
    // Stay in app — guest mode
  };

  const openNotifInbox = () => navigate('notif-inbox');

  const handlePremiumCheckout = async () => {
    try {
      const origin = getCurrentOrigin();
      const res = await api.createPremiumCheckout(BACKEND_URL);
      if (res.data?.url) navigateTo(res.data.url);
    } catch (e) { console.error('Checkout error:', e); }
  };

  // Check for premium success redirect or standalone countdown
  useEffect(() => {
    const params = getSearchParams();
    const sid = params.get('session_id');
    if (sid && getCurrentPath() === '/premium-success') {
      const confirmPayment = async (retries = 5) => {
        for (let i = 0; i < retries; i++) {
          try {
            const res = await api.confirmPremium(sid);
            if (res.data?.payment_status === 'paid') {
              await refreshData();
              window.history.replaceState({}, '', '/');
              return;
            }
          } catch (e) { console.error('Confirm attempt failed:', e); }
          await new Promise(r => setTimeout(r, 2000));
        }
        window.history.replaceState({}, '', '/');
      };
      confirmPayment();
    }
  }, []);

  // Standalone countdown page
  if (getCurrentPath() === '/countdown') {
    return <CountdownStandalone />;
  }

  if (phase === 'loading') return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: 'var(--base)' }}>
      <div className="pulse-a">{Ic.flame(32, GOLD)}</div>
    </div>
  );

  if (phase === 'debt-entry') return (
    <div style={{ maxWidth: 430, margin: '0 auto', background: 'var(--base)', minHeight: '100vh', position: 'relative' }}>
      <GrainOverlay />
      <DebtOnboarding onComplete={async () => {
        try { await api.seedData(); } catch { }
        try { const { data: d } = await api.getDebts(); setDebts(d); } catch { }
        setPhase('onboarding');
      }} />
    </div>
  );

  if (phase === 'onboarding') return (
    <div style={{ maxWidth: 430, margin: '0 auto', background: 'var(--base)', minHeight: '100vh', position: 'relative' }}>
      <GrainOverlay />
      <OnboardingScreen onComplete={() => setPhase('bank')} />
    </div>
  );

  if (phase === 'bank') return (
    <div style={{ maxWidth: 430, margin: '0 auto', background: 'var(--base)', minHeight: '100vh', position: 'relative' }}>
      <GrainOverlay />
      <BankScreen onComplete={() => { setPhase('app'); refreshData(); }} onSkip={() => { setPhase('app'); refreshData(); }} />
    </div>
  );

  const renderScreen = () => {
    switch (screen) {
      case 'payments': return <PaymentsScreen navigate={(s, d) => navigate(s, null, d)} />;
      case 'transaction-detail': return <TransactionDetailScreen payment={screenData} />;
      case 'manual-payment': return <ManualPaymentScreen debts={debts} onComplete={() => { goBack(); refreshData(); }} />;
      case 'badges': return <BadgesScreen />;
      case 'spending': return <SpendingScreen />;
      case 'spending-analysis': return <SpendingAnalysisScreen user={user} debts={debts} navigate={(s) => navigate(s)} />;
      case 'premium': return <PremiumScreen user={user} onCheckout={handlePremiumCheckout} />;
      case 'roundups': return <RoundUpsScreen />;
      case 'whatif': return <WhatIfScreen debts={debts} />;
      case 'debt-entry': return <DebtEntryScreen debts={debts} refreshData={refreshData} navigate={navigate} />;
      case 'debt-detail': return <DebtDetailScreen debt={screenData} onBack={goBack} refreshData={refreshData} />;
      case 'budget': return <BudgetScreen />;
      case 'timeline': return <TimelineScreen />;
      case 'literacy': return <LiteracyScreen />;
      case 'challenges': return <ChallengesScreen />;
      case 'countdown': return <CountdownScreen />;
      case 'notifications': return <NotificationsScreen />;
      case 'notif-inbox': return <NotificationInbox onClose={goBack} onNavigate={(s) => navigate(s)} />;
      case 'export': return <ExportScreen />;
      case 'referral': return <ReferralScreen />;
      case 'help': return <HelpScreen />;
      case 'privacy': return <PrivacyScreen onBack={goBack} />;
      case 'terms': return <TermsScreen onBack={goBack} />;
      case 'delete-account': return <DeleteAccountScreen onBack={goBack} onDelete={handleLogout} />;
      case 'merchant-rules': return <MerchantRulesScreen />;
      case 'set-password': return <SetPasswordScreen navigate={navigateTo} user={user} />;
      case 'auth-modal': return (
        <div style={{ minHeight: '100vh', background: 'var(--base)', position: 'relative' }}>
          <GrainOverlay />
          <AuthScreen onAuth={(userData) => { handleAuth(userData); setScreen(null); }} />
        </div>
      );
      case 'bank-manage': return <BankManageScreen />;
      case 'celebration': return <CelebrationScreen data={screenData} />;
      case 'comeback': return <ComebackScreen />;
      case 'finale': return <FinaleScreen data={screenData} />;
      case 'yearreview': return <YearReviewScreen />;
      case 'leaderboard': return <LeaderboardScreen />;
      case 'activity': return <ActivityScreen />;
      case 'points': return <PointsScreen />;
      case 'streaks': return <StreakScreen />;
      case 'recurring': return <RecurringScreen debts={debts} />;
      case 'custom-strategy': return <CustomStrategyScreen debts={debts} refreshData={refreshData} />;
      case 'edit-profile': return <FinancialProfileWizard 
        initialData={editProfileData}
        onComplete={() => { goBack(); setEditProfileData(null); }}
        onSkip={() => { goBack(); setEditProfileData(null); }}
      />;
      default: return null;
    }
  };

  const renderTab = () => {
    switch (tab) {
      case 'home': return <HomeScreen navigate={(s, t, data) => t ? navigate(null, t) : navigate(s, null, data)} debts={debts} user={user} />;
      case 'strategy': return <StrategyScreen navigate={(s) => navigate(s)} debts={debts} />;
      case 'insights': return <InsightsScreen navigate={(s) => navigate(s)} debts={debts} user={user} />;
      case 'coach': return <CoachScreen user={user} navigate={(s) => navigate(s)} />;
      case 'settings': return <SettingsScreen navigate={(s) => navigate(s)} user={user} onLogout={handleLogout} onLogin={handleAuth} bankStatus={bankStatus} />;
      default: return null;
    }
  };

  const screenTitle = {
    payments: 'Payments', badges: 'Badges', spending: 'Spending', 'spending-analysis': 'Spending Analysis', premium: 'Premium',
    roundups: 'Round-Ups', whatif: 'What-If', 'debt-entry': 'Manage Debts', 'debt-detail': 'Debt Details', budget: 'Budget', timeline: 'Payoff Timeline', literacy: 'Learn', challenges: 'Daily Challenges', countdown: 'Countdown',
    notifications: 'Notifications', 'notif-inbox': 'Reminders', export: 'Export', referral: 'Referral', help: 'Help & FAQ',
    'bank-manage': 'Bank Connection', 'merchant-rules': 'Merchant Rules', celebration: 'Debt Killed', comeback: 'Welcome Back',
    finale: 'Debt Free!', yearreview: 'Year Review', 'transaction-detail': 'Transaction',
    'manual-payment': 'Log Payment',
    leaderboard: 'Leaderboard', activity: 'Activity Feed', points: 'Points & Streaks',
    recurring: 'Recurring Payments', 'custom-strategy': 'Custom Strategy',
    'edit-profile': 'Financial Profile',
    'auth-modal': 'Sign In',
  };

  return (
    <div data-testid="app-container" style={{ maxWidth: 430, margin: '0 auto', background: 'var(--base, #111010)', minHeight: '100vh', display: 'flex', flexDirection: 'column', position: 'relative' }}>
      <GrainOverlay />
      {/* Header */}
      {screen ? (
        <div className="safe-top" data-testid="sub-screen-header" style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '16px 28px', position: 'relative', zIndex: 2, borderBottom: `1px solid var(--faint, ${FAINT})` }}>
          <button onClick={goBack} data-testid="back-btn" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
            <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke={'var(--mute)'} strokeWidth="1.6" strokeLinecap="round"><path d="M19 12H5M12 19l-7-7 7-7" /></svg>
          </button>
          <Mo style={{ fontSize: 10, fontWeight: 600, color: 'var(--mute)', letterSpacing: 2, textTransform: 'uppercase' }}>{screenTitle[screen] || screen}</Mo>
        </div>
      ) : (
        <div className="safe-top" data-testid="main-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 28px', position: 'relative', zIndex: 2 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span className="str-b">{Ic.flame(16, GOLD)}</span>
            <Dp style={{ fontSize: 14, fontWeight: 800, color: 'var(--light)', letterSpacing: -0.5 }}>DETTIO</Dp>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <NotifBell count={unreadCount} onClick={openNotifInbox} />
            {user?.plan === 'premium' ? (
              <button onClick={() => navigate('premium')} data-testid="premium-badge-header" style={{
                display: 'flex', alignItems: 'center', gap: 4, padding: '4px 10px',
                background: `linear-gradient(135deg, ${GOLD}20, ${EMBER}15)`,
                border: `1px solid ${GOLD}40`, cursor: 'pointer',
                position: 'relative', overflow: 'hidden'
              }}>
                <svg width={12} height={12} viewBox="0 0 24 24" fill={GOLD} stroke="none">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87L18.18 22 12 18.56 5.82 22 7 14.14l-5-4.87 6.91-1.01z"/>
                </svg>
                <Mo style={{ fontSize: 10, fontWeight: 700, color: 'var(--gold)', letterSpacing: 1.5 }}>PRO</Mo>
                <div style={{
                  position: 'absolute', top: 0, left: '-100%', width: '60%', height: '100%',
                  background: `linear-gradient(90deg, transparent, ${GOLD}15, transparent)`,
                  animation: 'premiumShimmer 3s infinite'
                }} />
              </button>
            ) : (
              <button onClick={() => navigate('premium')} data-testid="free-badge-header" style={{
                display: 'flex', alignItems: 'center', gap: 4, padding: '4px 8px',
                background: 'transparent', border: `1px solid ${GHOST}`,
                cursor: 'pointer'
              }}>
                <Mo style={{ fontSize: 10, fontWeight: 600, color: 'var(--mute)', letterSpacing: 1 }}>FREE</Mo>
              </button>
            )}
          </div>
        </div>
      )}
      {/* Content */}
      <div style={{ flex: 1, overflowY: 'auto', position: 'relative' }}>
        {screen ? renderScreen() : renderTab()}
      </div>
      {/* Bottom Nav */}
      {!screen && (
        <div data-testid="bottom-nav" style={{ display: 'flex', borderTop: '1px solid var(--faint)', background: 'var(--base)', position: 'relative', zIndex: 2 }}>
          {TABS.map(t => (
            <button 
              key={t} 
              onClick={() => { setTab(t); setScreen(null); setScreenData(null); setScreenStack([]); }} 
              data-testid={`nav-${t}`}
              className={`nav-item ${tab === t ? 'active' : ''}`}
              style={{ 
                flex: 1, 
                padding: '12px 0 10px', 
                background: 'transparent', 
                border: 'none', 
                cursor: 'pointer', 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                gap: 4, 
                opacity: tab === t ? 1 : 0.4,
                transition: 'all 0.2s ease'
              }}>
              {TAB_ICONS[t](20, tab === t ? '#00FFCC' : MUTE)}
              <Mo style={{ fontSize: 10, fontWeight: 600, color: tab === t ? '#00FFCC' : MUTE, letterSpacing: 1 }}>{TAB_LABELS[t].toUpperCase()}</Mo>
            </button>
          ))}
        </div>
      )}
      {/* Toast Notification */}
      {toast && <NotifToast notification={toast} onDismiss={() => setToast(null)} />}
      {/* Offline / Saved Data Banner */}
      {isOffline && (
        <div data-testid="offline-banner" style={{
          position: 'fixed', top: 0, left: '50%', transform: 'translateX(-50%)',
          width: '100%', maxWidth: 430,
          padding: '10px 20px',
          background: 'rgba(255, 160, 50, 0.12)',
          borderBottom: '1px solid rgba(255,160,50,0.3)',
          backdropFilter: 'blur(10px)',
          zIndex: 200,
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        }}>
          <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="#FFA032" strokeWidth="2" strokeLinecap="round">
            <path d="M1 6s4-2 11-2 11 2 11 2" /><path d="M5 10s3-1.5 7-1.5 7 1.5 7 1.5" />
            <line x1="3" y1="3" x2="21" y2="21" />
          </svg>
          <Mo style={{ fontSize: 11, color: '#FFA032', letterSpacing: 0.5 }}>
            Showing saved data · Connect to sync latest
          </Mo>
        </div>
      )}
      {/* Install App Banner */}
      {showInstallBanner && (
        <div data-testid="install-banner" style={{
          position: 'fixed', bottom: 70, left: '50%', transform: 'translateX(-50%)',
          width: 'calc(100% - 40px)', maxWidth: 400, padding: '16px 20px',
          background: 'var(--surface)', border: `1px solid ${GOLD}30`,
          boxShadow: '0 8px 32px rgba(0,0,0,0.4)', zIndex: 100,
          display: 'flex', alignItems: 'center', gap: 14,
        }}>
          <div style={{ width: 40, height: 40, background: `${GOLD}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            {Ic.flame(20, GOLD)}
          </div>
          <div style={{ flex: 1 }}>
            <Dp style={{ fontSize: 14, fontWeight: 700, color: 'var(--light)', display: 'block' }}>Install Dettio</Dp>
            <Bd style={{ fontSize: 11, color: 'var(--mute)', display: 'block', marginTop: 2, lineHeight: 1.4 }}>Add to your home screen for the best experience</Bd>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flexShrink: 0 }}>
            <button onClick={async () => {
              if (installPrompt) {
                installPrompt.prompt();
                const result = await installPrompt.userChoice;
                if (result.outcome === 'accepted') setShowInstallBanner(false);
              }
            }} data-testid="install-btn" style={{
              padding: '8px 14px', background: GOLD, border: 'none', color: BASE,
              fontFamily: MONO, fontSize: 9, fontWeight: 700, cursor: 'pointer', letterSpacing: 0.5,
            }}>INSTALL</button>
            <button onClick={() => {
              setShowInstallBanner(false);
              storage.setString('install-dismissed', 'true');
            }} data-testid="dismiss-install-btn" style={{
              padding: '4px', background: 'transparent', border: 'none',
              color: 'var(--ghost)', fontFamily: MONO, fontSize: 10, cursor: 'pointer',
            }}>LATER</button>
          </div>
        </div>
      )}
    </div>
  );
}
